/**
 * Service layer — Milestones domain
 *
 * Owns: Project Deliverables / milestones (tblFuiDpahqCL4eIK),
 *       Project Deliverable Types / templates (tblfulsOyOyjOGXab),
 *       Agency Approvals (tbl2Yu7YtDZ6tFFhI),
 *       Delta Revisions (tbl2sdOXCdvS2R5DL)
 */

import { apiGet, apiGetRecord, apiPost, apiPatch } from './_api.js'
import { getProjectSchedule, refreshMilestoneMirror } from './schedule.js'
import { ensureHearingDate } from './keyDates.js'

const TBL_DEL              = 'tblFuiDpahqCL4eIK'
const TBL_TPL              = 'tblfulsOyOyjOGXab'
const TBL_AGENCY_APPROVALS = 'tbl2Yu7YtDZ6tFFhI'
const TBL_DELTA_REVISIONS  = 'tbl2sdOXCdvS2R5DL'

// ── Stage 1 read-flip (FLAGGED) ──────────────────────────────────────────────
// Read the milestone core from Supabase (schedule_milestones) and return the SAME Airtable-shaped
// { id, fields } objects the outline/components already consume, so callers are unaffected. Bridged:
// link fields stay Airtable record ids; the deliverable-type NAME comes from the deliverable_types join
// (added server-side in api/schedule.js). Derived fields not in Supabase (checklist %, RASCI text, fee
// rollups, Urgency/Priority, LST/EFT) are intentionally absent here — they're loaded separately or
// computed; parity-checked per project before the flag becomes the default. See docs/SCHEDULING_MIGRATION.md.

/** Is the Supabase milestone read path enabled? Off by default; flip via localStorage('aj-ms-supabase'). */
export function isMilestoneSupabaseRead() {
  try { return localStorage.getItem('aj-ms-supabase') === '1' } catch { return false }
}

const arr1 = v => (v == null || v === '' ? undefined : [v])

// ── Date-relative urgency (computed, not stored — mirrors the Airtable formulas) ──
function parseUTCDate(s) { if (!s) return null; const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s)); return m ? new Date(Date.UTC(+m[1], +m[2] - 1, +m[3])) : null }
// Working days from `fromYmd` to `toYmd` (weekends excluded), signed; mirrors Airtable's
// WORKDAY_DIFF(today, DATEADD(LFT, 8h)) — INCLUSIVE of both endpoints (the +8h nudge makes the LFT day
// count), so we count [a, b] inclusive, not the exclusive span. (Verified against __ajMsParity.)
function workdayDiff(fromYmd, toYmd) {
  const a0 = parseUTCDate(fromYmd), b0 = parseUTCDate(toYmd)
  if (!a0 || !b0) return null
  let a = a0, b = b0, sign = 1
  if (b0 < a0) { a = b0; b = a0; sign = -1 }
  let days = 0; const d = new Date(a)
  while (d.getTime() <= b.getTime()) { const wd = d.getUTCDay(); if (wd !== 0 && wd !== 6) days++; d.setUTCDate(d.getUTCDate() + 1) }
  return sign * days
}
// Urgency → message buckets (ported verbatim from the Airtable Urgency Message formula).
function urgencyMessageFor(u) {
  if (u < 0) return '🚫 IMPOSSIBLE - COURSE CORRECT'
  if (u <= 3) return '🔥 URGENT'
  if (u <= 5) return '🌡️ Heating Up'
  if (u <= 10) return '⏳ In the Queue'
  if (u <= 20) return '🛤️ On Horizon'
  if (u <= 40) return '📅 Next Month'
  if (u <= 60) return '📅📅 Two Months Out'
  if (u <= 80) return '📅📅📅 Three Months Out'
  return '📅➡️ Next Quarter'
}

/** Map a Supabase schedule_milestones row → an Airtable-shaped { id, fields } milestone record. */
export function supaRowToMilestone(row) {
  const f = {}
  const set = (k, v) => { if (v !== null && v !== undefined && v !== '') f[k] = v }
  set('Full Deliverable Name', row.full_deliverable_name)
  set('Name', row.name)
  set('Project Phase', row.project_phase_name)
  set('Earliest Start (EST)', row.earliest_start)
  set('Latest Finish (LFT)', row.latest_finish)
  set('Duration', row.duration_days)
  if (row.date_pinned) f['Date Pinned'] = true
  set('Milestone Behavior', arr1(row.milestone_behavior))
  set('Posted Date', row.posted_date)
  set('Subtitle', row.subtitle)
  set('Deliverable for', row.deliverable_for)
  set('Milestone Type', row.milestone_type)
  set('Notes', row.notes)
  set('Before We Can Start', row.before_we_can_start)
  set('Team Sprints', row.team_sprints)
  set('Slack Thread Timestamp', row.slack_thread_ts)
  set('Google URL', row.google_url)
  set('Google Drive URL', row.google_drive_url)
  set('Hours Used', row.hours_used)
  set('Schedule Pushes', row.schedule_pushes)
  set('Schedule Pulls', row.schedule_pulls)
  set('Last Pushed', row.last_pushed)
  set('Last Schedule Push', row.last_schedule_push)
  if (row.focus_deliverable) f['Focus Deliverable'] = true
  if (row.in_sprint_planner) f['In Sprint Planner'] = true
  if (row.ready_for_checklist) f['Ready for Checklist'] = true
  if (row.quality_check) f['Quality Check'] = true
  if (row.client_approved) f['Client Approved'] = true
  if (row.auto_schedule_complete) f['Auto Schedule Complete'] = true
  if (row.created_by_automation) f['Created by Automation'] = true
  if (row.create_slack_thread) f['Create Slack Thread'] = true
  set('Project', arr1(row.project_id))
  set('Phase', arr1(row.phase_id))
  set('Deliverable', arr1(row.deliverable_type_airtable_id))
  set('Name (from Deliverable)', arr1(row.deliverable_type_name))
  set('Predecessor', arr1(row.predecessor_airtable_id))
  set('Days Budgeted', row.days_budgeted_snapshot)
  set('Created', row.airtable_created_at)
  // Display gaps (1.4a): imported derived/lookup values read directly off the record.
  set('Project Name', arr1(row.project_name))
  set('Responsible (Text)', row.responsible_text)
  set('Responsible Contact Name (from RASCI Assignments)', arr1(row.responsible_contact_name))
  set('Progress', row.progress)
  set('Percent Complete Checklist Items', row.percent_complete_checklist_items)
  set('Completed Checklist Items', row.completed_checklist_items)
  set('Incomplete Checklist Items', row.incomplete_checklist_items)
  if (row.agency_approvals && row.agency_approvals.length) f['Agency Approvals'] = row.agency_approvals
  if (row.delta_revisions_plan_check && row.delta_revisions_plan_check.length) f['Delta Revisions - Plan Check'] = row.delta_revisions_plan_check
  if (row.delta_revisions_permit && row.delta_revisions_permit.length) f['Delta Revisions - Permit'] = row.delta_revisions_permit
  // Urgency Message + Slack are date-relative — compute fresh (don't store). All imported rows are
  // Executed projects, so the On-Hold/Cancelled/Completed stage overrides never apply here.
  if (!row.latest_finish) {
    f['Urgency Message'] = 'Not Scheduled'
  } else {
    const daysLeft = workdayDiff(new Date().toISOString().slice(0, 10), row.latest_finish)
    if (daysLeft == null) {
      f['Urgency Message'] = 'Not Scheduled'
    } else {
      const slack = daysLeft - (row.days_budgeted_snapshot != null ? Number(row.days_budgeted_snapshot) : 0)
      f['Slack'] = slack
      f['Urgency Message'] = row.posted_date ? '✔️ Posted' : urgencyMessageFor(slack + 1)
    }
  }
  return { id: row.airtable_id, fields: f }
}

/** Read a project's milestones from Supabase as Airtable-shaped records (the read-flip path). */
export async function getProjectMilestonesSupabase(projectId) {
  const { milestones } = await getProjectSchedule(projectId)
  return (milestones || []).map(supaRowToMilestone)
}

/** All milestones for a project (full records), sorted by Latest Finish. Group to a phase by
 *  `m.fields['Project Phase'] === phase.fields['Phase Name']`. For the phase-landscape view. */
export function getProjectMilestones(projectId) {
  if (!projectId) return Promise.resolve([])
  const qs = 'filterByFormula=' + encodeURIComponent('FIND("' + projectId + '",ARRAYJOIN({Project Record UID from Project},","))')
    + '&sort%5B0%5D%5Bfield%5D=Latest+Finish+(LFT)&sort%5B0%5D%5Bdirection%5D=asc&maxRecords=120'
  return apiGet(TBL_DEL, qs).then(function (records) { return records || [] })
}

/**
 * Fetch all milestone templates, sorted by Name.
 * Returns raw Airtable records.
 */
export function getMilestoneTemplates() {
  return apiGet(
    TBL_TPL,
    'sort%5B0%5D%5Bfield%5D=Name&sort%5B0%5D%5Bdirection%5D=asc&maxRecords=100'
  )
}

/**
 * All milestones in a project built from the "Architects Supplemental Instructions" template —
 * the candidate list for the manual ASI<->milestone link-repair modal (AsiNode's "Edit milestone
 * link" action). {Name (from Deliverable)} is a lookup field (plain text via ARRAYJOIN), so this
 * filters by name text directly — no record-ID-vs-primary-value gotcha to work around here.
 * Returns raw Airtable records, sorted by Latest Finish desc (most recent first).
 */
export function getProjectAsiMilestones(projectId) {
  if (!projectId) return Promise.resolve([])
  const formula = 'AND(FIND("' + projectId + '",ARRAYJOIN({Project Record UID from Project},",")),'
    + 'FIND("Architects Supplemental Instructions",ARRAYJOIN({Name (from Deliverable)},",")))'
  return apiGet(
    TBL_DEL,
    'filterByFormula=' + encodeURIComponent(formula)
    + '&sort%5B0%5D%5Bfield%5D=Latest+Finish+(LFT)&sort%5B0%5D%5Bdirection%5D=desc&maxRecords=100'
  )
}

/**
 * Create a new milestone record.
 * @param {object} fields — Airtable-ready field map
 * @returns {Promise<string>} The new record's ID
 * @throws if Airtable returns an error
 */
export async function createMilestone(fields) {
  const res = await apiPost(TBL_DEL, fields)
  if (res.error) throw new Error(res.error)
  // Best-effort: link this milestone to the project's shared "Scheduled Hearing Date" key date
  // when its type is Public Hearing Preparation / Public Hearing (docs/KEY_DATES.md). The server
  // decides — this fires unconditionally and no-ops for every other type, so every creation path
  // funneling through this one function (the wizard + the ASI/RFI/Submittal modals) gets it for
  // free without each needing to know the two hearing-type record ids itself. Never blocks or
  // fails the create — the milestone is fully usable either way.
  const projId = Array.isArray(fields['Project']) ? fields['Project'][0] : null
  const typeId = Array.isArray(fields['Deliverable']) ? fields['Deliverable'][0] : null
  if (projId && typeId) ensureHearingDate(projId, res.id, typeId, fields['Subtitle'] || '').catch(() => {})
  return res.id
}

/**
 * Post a milestone — writes Posted Date and optional linked records.
 *
 * The LFT/Posted-date relationship is now handled entirely in code (see the post modal +
 * services/schedule.js `syncFinishAndCascade`): the legacy `Sync Latest Finish to Post Date` checkbox
 * (and its Airtable automation) is RETIRED — we no longer write that field. When the user chooses to
 * sync the schedule to a posted date that differs from the LFT, the modal moves the LFT and re-flows
 * downstream explicitly; otherwise the schedule is left untouched (e.g. a late-logged on-time post).
 * @param {string} msId
 * @param {{ date: string, agencyId?: string, planCheckId?: string, permitRevId?: string }} opts
 * @returns {Promise} Raw Airtable PATCH response
 */
export function postMilestone(msId, { date, agencyId, planCheckId, permitRevId }) {
  const fields = { 'Posted Date': date }
  if (agencyId)    fields['Agency Approvals']            = [agencyId]
  if (planCheckId) fields['Delta Revisions - Plan Check'] = [planCheckId]
  if (permitRevId) fields['Delta Revisions - Permit']     = [permitRevId]
  // Cutover slice one: the mirror learns about the post immediately (the Flight Board and the
  // nightly scheduler read posted_date from Supabase now), not at the next daily sync.
  return apiPatch(TBL_DEL, msId, fields).then(rec => { refreshMilestoneMirror([msId]); return rec })
}

/**
 * Move a milestone to a different phase.
 * @param {string} msId
 * @param {string} phaseId — the target phase record ID
 * @param {string} phaseName — the Phase Name text (kept in sync on the milestone)
 * @returns {Promise} Raw Airtable PATCH response
 */
export function moveMilestoneToPhase(msId, phaseId, phaseName) {
  return apiPatch(TBL_DEL, msId, {
    'Phase':         [phaseId],
    'Project Phase': phaseName,
  })
}

/**
 * Edit a milestone's template (the linked Project Deliverable Type) and/or subtitle.
 * `Deliverable` (multipleRecordLinks → tblfulsOyOyjOGXab) is the template; `Subtitle` (singleLineText)
 * is the free-text subtitle. Returns the patched record (formula fields like Full Deliverable Name
 * are recomputed by Airtable in the response).
 * @param {string} msId
 * @param {{ templateId?: string, subtitle?: string }} fields
 */
export function updateMilestoneMeta(msId, { templateId, subtitle } = {}) {
  const f = {}
  if (templateId) f['Deliverable'] = [templateId]
  if (subtitle !== undefined) f['Subtitle'] = subtitle || ''
  return apiPatch(TBL_DEL, msId, f)
}

/**
 * Update the Latest Finish (LFT) due date on a milestone.
 * @param {string} msId — Project Deliverable record ID
 * @param {string} newDate — ISO date string YYYY-MM-DD
 */
export function updateMilestoneLft(msId, newDate) {
  return apiPatch(TBL_DEL, msId, { 'Latest Finish (LFT)': newDate })
}

/**
 * Fetch Agency Approvals linked to a project (permit milestone variant).
 * @param {string} projectId
 * @returns {Promise<{id: string, name: string}[]>}
 */
export async function getAgencyApprovals(projectId) {
  // BUG FIX (2026-07): this previously filtered on `Project Record ID (from Permits)`, a field
  // that does not exist on Agency Approvals (confirmed against the live schema) — every call
  // silently returned zero rows, for every project, since the field was introduced. The correct
  // field is the same one services/permits.js's getProjectAgencyReviews already uses correctly.
  const records = await apiGet(
    TBL_AGENCY_APPROVALS,
    'filterByFormula=' + encodeURIComponent('FIND("' + projectId + '",ARRAYJOIN({Project Record UID (from Project) (from Permits)},","))') + '&maxRecords=50'
  )
  return records.map(r => ({ id: r.id, name: r.fields['Name'] || r.id }))
}

/**
 * Fetch Delta Revisions linked to a project (revision milestone variant).
 * Returns two arrays split by type.
 * @param {string} projectId
 * @returns {Promise<{ planCheck: {id, name}[], permit: {id, name}[] }>}
 */
export async function getDeltaRevisions(projectId) {
  const records = await apiGet(
    TBL_DELTA_REVISIONS,
    'filterByFormula=' + encodeURIComponent('FIND("' + projectId + '",ARRAYJOIN({Project Record ID (from Permit)},","))') + '&maxRecords=100'
  )
  return {
    planCheck: records
      .filter(r => r.fields['Type'] === 'Plan Check Revision')
      .map(r => ({ id: r.id, name: r.fields['Name'] || r.id })),
    permit: records
      .filter(r => r.fields['Type'] === 'Permit Revision')
      .map(r => ({ id: r.id, name: r.fields['Name'] || r.id })),
  }
}

/**
 * Fetch just enough of a milestone to render a compact reference row (e.g. the
 * milestone a Delta Revision is linked to, in PlanCheckSections.jsx) — not the
 * full record ctx.state.milestones carries, which may not be loaded yet if the
 * caller reached the milestone via Permits rather than Schedule.
 * @param {string} msId
 * @returns {Promise<{id, name, phaseName, typeName, posted}|null>}
 */
export async function getMilestoneBasicInfo(msId) {
  const rec = await apiGetRecord(TBL_DEL, msId)
  if (!rec) return null
  const f = rec.fields || {}
  return {
    id:        rec.id,
    name:      f['Full Deliverable Name'] || f['Name'] || 'Milestone',
    phaseName: f['Project Phase'] || '',
    typeName:  (f['Name (from Deliverable)'] || [])[0] || '',
    posted:    !!f['Posted Date'],
    projectName: (f['Project Name (from Project)'] || [])[0] || '',
    projectId: (f['Project'] || [])[0] || null,
  }
}

/**
 * Fetch the FULL milestone record — same {id, fields} shape as an entry in
 * ctx.state.milestones — as a fallback for callers that reach a milestone from OUTSIDE the
 * outline (2026-09-04: PostMilestoneModal opened from the Flight Board, whose own board data is
 * loaded independently of whatever project happens to be zoomed into in the outline). Unlike
 * getMilestoneBasicInfo's slim subset above, this returns every field PostMilestoneModal's own
 * gates (permit/revision pickers, sheet QC, journey eligibility, ...) read.
 * @param {string} msId
 * @returns {Promise<{id, fields}|null>}
 */
export async function getMilestoneRecord(msId) {
  return apiGetRecord(TBL_DEL, msId)
}
